import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { ListItemSpacing, ListVariant } from '@aurora/shared-client/components/common/List/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { RepliesFormat } from '@aurora/shared-generated/types/graphql-schema-types';
import ConfigHelper from '@aurora/shared-utils/helpers/ConfigHelper';
import dynamic from 'next/dynamic';
import React, { useContext, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { contextMessageToMessageView } from '../../../helpers/messages/MessageHelper/MessageHelper';
import type { ItemType } from '../../../types/enums';
import {
  MessageActionMenuItem,
  MessageViewStandardAvatarPosition,
  MessageViewVariant
} from '../../../types/enums';
import type {
  ContextMessageFragment,
  MessageParentFragment,
  MessageRepliesCountFragment
} from '../../../types/graphql-types';
import getConfig from 'next/config';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import { UrlObject, useContextObjectRefFromUrl } from '../../context/useContextObjectFromUrl';
import type { ItemViewTypeAndProps } from '../../entities/types';
import useInView from '../../useInView';
import useRepliesProperties from '../LinearReplyList/useRepliesProperties';
import MessageDeepLink from '../MessageDeepLink/MessageDeepLink';
import MessageReplyCallToAction from '../MessageReplyCallToAction/MessageReplyCallToAction';
import MessageView from '../MessageView/MessageView';
import defaultViewProps from '../MessageView/MessageViewStandard/MessageViewStandardDefaultProps';
import { useAutoScrollToReplies } from '../useAutoScrollToReplies';
import type { MessageWithRepliesWidgetProps } from '../types';
import localStyles from './TopicWithThreadedReplyListWidget.module.css';

const ThreadedReplyList = dynamic(
  () => import('../../messages/ThreadedReplyList/ThreadedReplyList')
);

const LinearReplyList = dynamic(() => import('../../messages/LinearReplyList/LinearReplyList'));

const { publicRuntimeConfig } = getConfig() ?? {};
const lazyFetchRepliesDefault = ConfigHelper.toBoolean(publicRuntimeConfig.lazyLoadReplies, true);
const pageSizeDefault = ConfigHelper.toNumber(publicRuntimeConfig.repliesPageSize, 10);

/**
 * Widget that displays a topic message, and its threaded replies. Supports
 * nested pagination on the threaded replies and sorting the replies
 * by date.
 *
 * @constructor
 *
 * @author Adam Ayres
 */
const TopicWithThreadedReplyListWidget: WidgetFC<MessageWithRepliesWidgetProps> = props => {
  const {
    editLevel,
    instanceId,
    className,
    listVariant = {
      type: ListVariant.UNSTYLED,
      props: {
        listItemSpacing: ListItemSpacing.SM
      }
    },
    viewVariant = {
      type: MessageViewVariant.STANDARD,
      props: {
        ...defaultViewProps,
        avatarPosition: MessageViewStandardAvatarPosition.TOP,
        useReplies: true,
        useRepliesCount: true,
        useSubject: false,
        useNodeLink: false,
        useKudosCount: true,
        useAcceptedSolutionButton: true,
        useTags: true,
        useSolvedBadge: false,
        adminActionMenuItems: [
          { item: MessageActionMenuItem.EDIT_INLINE },
          { item: MessageActionMenuItem.MOVE_COMMENT },
          { item: MessageActionMenuItem.ESCALATE_TO_SALESFORCE },
          { item: MessageActionMenuItem.COPY_LINK },
          { item: MessageActionMenuItem.SUBSCRIBE },
          { item: MessageActionMenuItem.DELETE }
        ],
        actionMenuItems: [
          { item: MessageActionMenuItem.REPORT_ABUSE },
          { item: MessageActionMenuItem.MARK_AS_APPROVED },
          { item: MessageActionMenuItem.REJECT },
          { item: MessageActionMenuItem.MARK_AS_NOT_SPAM },
          { item: MessageActionMenuItem.BAN_MEMBER },
          { item: MessageActionMenuItem.EDIT_BAN_MEMBER }
        ],
        className: 'lia-g-message-box',
        useAccordionForReply: true,
        flattenDepth: 7,
        useReadOnlyIcon: true,
        showEscalationBanner: true
      }
    },
    pagerVariant,
    pageSize = pageSizeDefault,
    lazyFetchReplies = lazyFetchRepliesDefault
  } = props;
  const cx = useClassNameMapper(localStyles);
  const { isCrawler, contextMessage: rootMessage } = useContext(AppContext);
  const { id: replyId } = useContextObjectRefFromUrl(UrlObject.REPLY);
  const [inViewRef, inView] = useInView<HTMLDivElement>();
  const showReplies = replyId === null;
  const editorRef = useRef(null);
  const autoScrollToReplies = useAutoScrollToReplies();

  const { repliesFormat, repliesFormatLoading } = useRepliesProperties(rootMessage?.board);

  if (repliesFormatLoading) {
    return null;
  }

  const isThreadedRepliesFormat = repliesFormat === RepliesFormat.Threaded;

  // Adjust the topic view variant props, consider exposing these as separate parameters
  // on this component for control via the Quilted page.
  const topicViewVariant: ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant> = {
    type: viewVariant.type,
    props: {
      ...viewVariant.props,
      useSubject: true,
      useSubjectLink: false,
      subjectAs: 'h1',
      subjectClassName: 'h3 lia-g-mt-10 lia-g-mb-20 text-body',
      className: cx(`${viewVariant?.props?.className ?? ''} lia-main-topic`),
      useSolvedBadge: true,
      actionMenuItems: null,
      avatarSize: IconSize.PX_50,
      useAccordionForReply: false,
      useReplies: false,
      useRepliesCount: false,
      useTextBody: true,
      usePreviewMedia: true,
      usePreview: !showReplies,
      clampBodyLines: showReplies ? null : 2
    }
  };

  const decoratedViewVariant = { ...viewVariant };
  decoratedViewVariant.props.className = cx(decoratedViewVariant.props.className);
  decoratedViewVariant.props.useRepliesCount =
    decoratedViewVariant.props.useRepliesCount && isThreadedRepliesFormat;
  decoratedViewVariant.props.useAccordionForReply =
    decoratedViewVariant.props.useAccordionForReply && isThreadedRepliesFormat;
  decoratedViewVariant.props.repliesFormat = repliesFormat;

  return (
    <EditableWidget props={{ editLevel, instanceId }}>
      <div className={cx(className)} ref={editorRef}>
        <MessageView entity={contextMessageToMessageView(rootMessage)} variant={topicViewVariant} />
        <div ref={inViewRef}>
          {showReplies ? (
            <>
              {!isCrawler &&
              !autoScrollToReplies &&
              lazyFetchReplies &&
              !inView ? null : repliesFormat === RepliesFormat.Threaded ? (
                <ThreadedReplyList
                  message={contextMessageToMessageView(rootMessage)}
                  listVariant={listVariant}
                  viewVariant={decoratedViewVariant}
                  pagerVariant={pagerVariant}
                  pageSize={pageSize}
                  useEmpty
                  editorRef={editorRef}
                />
              ) : (
                <LinearReplyList
                  message={
                    rootMessage as ContextMessageFragment &
                      MessageRepliesCountFragment &
                      MessageParentFragment
                  }
                  viewVariant={decoratedViewVariant}
                  listVariant={listVariant}
                  pagerVariant={pagerVariant}
                  useEmpty
                  editorRef={editorRef}
                />
              )}
              {
                <MessageReplyCallToAction
                  message={rootMessage}
                  className={cx(
                    {
                      'lia-uses-accordion':
                        viewVariant.props.useAccordionForReply && isThreadedRepliesFormat
                    },
                    'lia-reply-cta-bottom'
                  )}
                />
              }
            </>
          ) : (
            <>
              <MessageDeepLink rootMessage={rootMessage} viewVariant={viewVariant} />
              {<MessageReplyCallToAction message={rootMessage} />}
            </>
          )}
        </div>
      </div>
    </EditableWidget>
  );
};

export default TopicWithThreadedReplyListWidget;
